import { Step } from 'react-joyride';
import { IntlShape } from 'react-intl';

// --- Build a single step with i18n ---

function makeStep(
  intl: IntlShape,
  target: string,
  titleId: string,
  contentId: string,
  placement: Step['placement'] = 'bottom',
  disableBeacon = false
): Step {
  return {
    target,
    title: intl.formatMessage({ id: titleId }),
    content: intl.formatMessage({ id: contentId }),
    placement,
    ...(disableBeacon ? { disableBeacon: true } : {})
  };
}

// --- Tab key → i18n key mapping ---

const tabConfig: Record<string, { titleId: string; contentId: string }> = {
  list: { titleId: 'tourListTitle', contentId: 'tourListContent' },
  tree: { titleId: 'tourTreeTitle', contentId: 'tourTreeContent' },
  live: { titleId: 'tourLiveTitle', contentId: 'tourLiveContent' },
  overview: { titleId: 'tourOverviewTitle', contentId: 'tourOverviewContent' },
  batteryview: { titleId: 'tourBatteryviewTitle', contentId: 'tourBatteryviewContent' },
  pvview: { titleId: 'tourPvviewTitle', contentId: 'tourPvviewContent' },
  log: { titleId: 'tourLogTitle', contentId: 'tourLogContent' },
  information: { titleId: 'tourInformationTitle', contentId: 'tourInformationContent' },
  report: { titleId: 'tourReportTitle', contentId: 'tourReportContent' },
  manage: { titleId: 'tourManageTitle', contentId: 'tourManageContent' },
  configuration: { titleId: 'tourConfigurationTitle', contentId: 'tourConfigurationContent' },
  history: { titleId: 'tourHistoryTitle', contentId: 'tourHistoryContent' }
};

// Steps to skip inside a specific installation (already covered in the list-page tour)
const listPageOnlyTabs = new Set(['list', 'tree']);

// --- Build tour steps from tab value list ---

function buildTourSteps(intl: IntlShape, tabValues: string[], includeInstallationHint = false, isInsideInstallation = false): Step[] {
  const steps: Step[] = [];
  if (!isInsideInstallation) {
    steps.push(makeStep(intl, '[data-tour="language-selector"]', 'tourLanguageTitle', 'tourLanguageContent', 'bottom', true));
  }
  for (const value of tabValues) {
    if (isInsideInstallation && listPageOnlyTabs.has(value)) continue;
    const cfg = tabConfig[value];
    if (cfg) {
      steps.push(makeStep(intl, `#tour-tab-${value}`, cfg.titleId, cfg.contentId, 'bottom', steps.length === 0));
    }
  }
  if (includeInstallationHint && !isInsideInstallation) {
    steps.push(makeStep(intl, '#tour-tab-list', 'tourExploreTitle', 'tourExploreContent'));
  }
  return steps;
}

// --- Sodistore Home (product 2) ---

export const buildSodiohomeCustomerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'live', 'overview', 'information', 'report'
], false, inside);

export const buildSodiohomePartnerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'live', 'overview', 'batteryview', 'log', 'information', 'report'
], true, inside);

export const buildSodiohomeAdminTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'live', 'overview', 'batteryview', 'log', 'manage', 'information', 'configuration', 'history', 'report'
], true, inside);

// --- Salimax (product 0) / Sodistore Max (product 3) ---

export const buildSalimaxCustomerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'live', 'overview', 'information'
], false, inside);

export const buildSalimaxPartnerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'live', 'overview', 'batteryview', 'pvview', 'information'
], true, inside);

export const buildSalimaxAdminTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'live', 'overview', 'batteryview', 'manage', 'log', 'information', 'configuration', 'history', 'pvview'
], true, inside);

// --- Sodistore Grid (product 4) — same as Salimax but no PV View ---

export const buildSodistoregridCustomerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'live', 'overview', 'information'
], false, inside);

export const buildSodistoregridPartnerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'live', 'overview', 'batteryview', 'information'
], true, inside);

export const buildSodistoregridAdminTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'live', 'overview', 'batteryview', 'manage', 'log', 'information', 'configuration', 'history'
], true, inside);

// --- Salidomo (product 1) ---

export const buildSalidomoCustomerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'batteryview', 'overview', 'information'
], false, inside);

export const buildSalidomoPartnerTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'batteryview', 'overview', 'information'
], true, inside);

export const buildSalidomoAdminTourSteps = (intl: IntlShape, inside = false) => buildTourSteps(intl, [
  'list', 'tree', 'batteryview', 'overview', 'log', 'manage', 'information', 'history'
], true, inside);
